import { Button, Card, Muted, Stack, color, space, status } from '@biopeak/ui';
import { useEffect, useState } from 'react';
import { useLatestAnalysis } from '../lib/analysis.ts';
import { useSession } from '../lib/session.tsx';
import { usePatientHeader, usePatientOverview, usePatientTrend } from '../lib/patient.ts';
import {
  useApproveReport,
  useGenerateReport,
  useReports,
  useSaveReport,
  type ReportContent,
  type TextoSuspeito,
} from '../lib/report.ts';
import { ReportDocument, type NumeroDestaque } from './ReportDocument.tsx';

/**
 * Relatório do paciente: gerar, revisar, aprovar, imprimir.
 *
 * O fluxo é deliberadamente em quatro passos, e não um botão só. O texto vai
 * ser lido por uma pessoa sobre a própria saúde, fora da consulta, sem ninguém
 * ao lado para contextualizar — o mesmo raciocínio que fez a revisão de exames
 * ter um portão. A máquina propõe, o médico assume.
 *
 * O botão de imprimir só aparece depois de aprovado, porque o PDF é o artefato
 * que sai daqui: liberar a impressão do rascunho tornaria a aprovação
 * decorativa.
 */
export function PatientReport({ patientId }: { patientId: string }) {
  const header = usePatientHeader(patientId);
  const { doctor } = useSession();
  const analise = useLatestAnalysis(patientId);
  const relatorios = useReports(patientId);
  const gerar = useGenerateReport(patientId);
  const salvar = useSaveReport(patientId);
  const aprovar = useApproveReport(patientId);

  const relatorio = relatorios.data?.[0];
  const [conteudo, setConteudo] = useState<ReportContent | null>(null);
  const [recado, setRecado] = useState('');
  const [suspeitos, setSuspeitos] = useState<TextoSuspeito[]>([]);

  // Dados do infográfico. A janela é a MESMA da análise que originou o texto —
  // um gráfico de 90 dias ao lado de um texto escrito sobre 30 mostraria uma
  // subida que o parágrafo não menciona, e quem lê não teria como saber por quê.
  const janela = analise.data?.window_days ?? 90;
  const overview = usePatientOverview(patientId, janela);
  const fcTrend = usePatientTrend(patientId, 'resting_heart_rate', janela);
  const sonoTrend = usePatientTrend(patientId, 'total_sleep_time', janela);
  const passosTrend = usePatientTrend(patientId, 'steps', janela);

  // Carrega o relatório do banco para o estado de edição quando ele troca.
  useEffect(() => {
    if (!relatorio) return;
    setConteudo(relatorio.content as unknown as ReportContent);
    setRecado(relatorio.doctor_message ?? '');
  }, [relatorio?.id]);

  if (relatorios.isLoading || analise.isLoading) return <Muted>Carregando…</Muted>;

  // Sem análise não há relatório: ele é derivado dela, e gerar um texto sobre
  // um período que ninguém analisou seria inventar a fonte.
  if (!analise.data) {
    return (
      <Card>
        <Stack gap={space.sm}>
          <span style={{ fontSize: '17px', fontWeight: 600 }}>Relatório do paciente</span>
          <Muted style={{ fontSize: '14px' }}>
            Gere primeiro a análise do período, na aba Visão geral. O relatório é
            escrito a partir dela, usando os mesmos números já verificados.
          </Muted>
        </Stack>
      </Card>
    );
  }

  const aprovado = Boolean(relatorio?.approved_at);

  // Só entra gráfico com dado suficiente para ter forma. Uma série de dois
  // pontos vira uma reta que sugere tendência onde não há — e num documento
  // sem ninguém ao lado para relativizar, isso é pior que a ausência.
  const graficos = [
    montarGrafico('Frequência cardíaca de repouso', 'bpm', fcTrend.data),
    montarGrafico('Horas de sono por noite', 'h', sonoTrend.data, (v) => v / 3600),
    montarGrafico('Passos por dia', 'passos', passosTrend.data),
  ].filter((g): g is NonNullable<typeof g> => g !== null);

  const o = overview.data;
  // `&&` devolve `false` quando a condição falha, e o TS infere `boolean` no
  // array — daí o tipo explícito em vez de confiar na inferência do filter.
  const numeros: (NumeroDestaque | false | null | undefined)[] = [
    o?.heart_rate?.resting != null && {
      rotulo: 'FC de repouso',
      valor: String(Math.round(o.heart_rate.resting)),
      legenda: 'batimentos por minuto',
    },
    o?.sleep && o.sleep.nights > 0 && {
      rotulo: 'Sono por noite',
      valor: formatarHoras(o.sleep.avg_total_s),
      legenda: `média de ${o.sleep.nights} noites`,
    },
    o?.sleep && o.sleep.nights > 0 && {
      rotulo: 'Eficiência do sono',
      valor: `${Math.round(o.sleep.avg_efficiency)}%`,
      legenda: 'do tempo na cama, dormindo',
    },
    o?.workouts && o.workouts.count > 0 && {
      rotulo: 'Treinos',
      valor: String(o.workouts.count),
      legenda: `no período`,
    },
  ];
  const numerosVisiveis = numeros.filter((n): n is NumeroDestaque => Boolean(n));

  return (
    <Stack gap={space.md}>
      <div className="nao-imprimir">
      <Card>
        <Stack gap={space.md}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: space.sm,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: '17px', fontWeight: 600 }}>Relatório do paciente</span>
            <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap' }}>
              <Button
                variant="secondary"
                disabled={gerar.isPending}
                onClick={() =>
                  gerar.mutate(analise.data!.id, {
                    onSuccess: (r) => setSuspeitos(r.unverified ?? []),
                  })
                }
              >
                {gerar.isPending
                  ? 'Escrevendo…'
                  : relatorio
                    ? 'Gerar de novo'
                    : 'Gerar rascunho'}
              </Button>

              {relatorio && !aprovado && (
                <Button
                  disabled={salvar.isPending || aprovar.isPending || !conteudo}
                  onClick={async () => {
                    if (!conteudo) return;
                    await salvar.mutateAsync({
                      reportId: relatorio.id,
                      content: conteudo,
                      doctorMessage: recado,
                    });
                    await aprovar.mutateAsync(relatorio.id);
                  }}
                >
                  {aprovar.isPending ? 'Aprovando…' : 'Aprovar e liberar'}
                </Button>
              )}

              {aprovado && (
                <Button onClick={() => window.print()}>Salvar em PDF</Button>
              )}
            </div>
          </div>

          {gerar.error && (
            <Aviso tom="red">{(gerar.error as Error).message}</Aviso>
          )}

          {!relatorio && !gerar.isPending && (
            <Muted style={{ fontSize: '14px' }}>
              O rascunho é escrito em linguagem de paciente a partir da análise já
              gerada — sem jargão, sem hipótese diagnóstica e sem as perguntas
              técnicas que ficam no seu painel. Você revisa e edita antes de qualquer
              coisa sair daqui.
            </Muted>
          )}

          {/* Números sem respaldo no brief. Aparecem ANTES do documento porque
              é o que o médico precisa conferir primeiro — e some sozinho
              quando o texto é regerado sem problemas. */}
          {suspeitos.length > 0 && (
            <Aviso tom="yellow">
              <strong>Confira estes números antes de aprovar.</strong> Eles aparecem no
              texto mas não foram encontrados no cálculo do período:{' '}
              {suspeitos
                .map((s) => `${s.campo} (${s.numeros_sem_respaldo.join(', ')})`)
                .join('; ')}
              .
            </Aviso>
          )}

          {aprovado && (
            <Aviso tom="green">
              Aprovado em{' '}
              {new Date(relatorio!.approved_at!).toLocaleString('pt-BR')}. O documento
              abaixo é o que o paciente recebe.
            </Aviso>
          )}

          {relatorio && !aprovado && conteudo && (
            <Editor
              conteudo={conteudo}
              recado={recado}
              onConteudo={setConteudo}
              onRecado={setRecado}
              salvando={salvar.isPending}
              onSalvar={() =>
                salvar.mutate({
                  reportId: relatorio.id,
                  content: conteudo,
                  doctorMessage: recado,
                })
              }
            />
          )}
        </Stack>
      </Card>
      </div>

      {relatorio && conteudo && (
        <ReportDocument
          content={conteudo}
          doctorMessage={recado}
          paciente={{
            nome: header.data?.full_name ?? '',
            nascimento: null,
          }}
          medico={doctor ? `Dr(a). ${doctor.full_name}` : ''}
          periodoDias={analise.data.window_days}
          geradoEm={new Date(relatorio.created_at).toLocaleDateString('pt-BR')}
          graficos={graficos}
          numeros={numerosVisiveis}
          exames={[]}
          dataExame={null}
        />
      )}
    </Stack>
  );
}

function Aviso({
  tom,
  children,
}: {
  tom: 'red' | 'yellow' | 'green';
  children: React.ReactNode;
}) {
  const cor = status[tom];
  return (
    <div
      style={{
        background: cor.bg,
        border: `1px solid ${cor.border}`,
        borderRadius: '8px',
        padding: space.sm,
        fontSize: '13px',
        color: cor.fg,
      }}
    >
      {children}
    </div>
  );
}

/** Edição campo a campo. O médico reescreve o que quiser antes de assumir. */
function Editor({
  conteudo,
  recado,
  onConteudo,
  onRecado,
  salvando,
  onSalvar,
}: {
  conteudo: ReportContent;
  recado: string;
  onConteudo: (c: ReportContent) => void;
  onRecado: (r: string) => void;
  salvando: boolean;
  onSalvar: () => void;
}) {
  return (
    <Stack gap={space.sm}>
      <Campo
        rotulo="Seu recado de abertura (opcional, aparece no topo assinado por você)"
        valor={recado}
        onChange={onRecado}
        linhas={3}
      />
      <Campo
        rotulo="Saudação"
        valor={conteudo.saudacao}
        onChange={(v) => onConteudo({ ...conteudo, saudacao: v })}
        linhas={2}
      />
      <Campo
        rotulo="Resumo do período"
        valor={conteudo.resumo}
        onChange={(v) => onConteudo({ ...conteudo, resumo: v })}
        linhas={4}
      />

      {conteudo.destaques.map((d, i) => (
        <Campo
          key={i}
          rotulo={`Destaque ${i + 1}: ${d.titulo}`}
          valor={d.texto}
          onChange={(v) => {
            const destaques = [...conteudo.destaques];
            destaques[i] = { ...d, texto: v };
            onConteudo({ ...conteudo, destaques });
          }}
          linhas={3}
        />
      ))}

      <Campo
        rotulo="Fechamento"
        valor={conteudo.fechamento}
        onChange={(v) => onConteudo({ ...conteudo, fechamento: v })}
        linhas={2}
      />

      <Button variant="secondary" onClick={onSalvar} disabled={salvando}>
        {salvando ? 'Salvando…' : 'Salvar rascunho'}
      </Button>
    </Stack>
  );
}

function Campo({
  rotulo,
  valor,
  onChange,
  linhas,
}: {
  rotulo: string;
  valor: string;
  onChange: (v: string) => void;
  linhas: number;
}) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ fontSize: '13px', color: color.textSecondary }}>{rotulo}</span>
      <textarea
        value={valor}
        rows={linhas}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          marginTop: '4px',
          padding: space.sm,
          fontSize: '14px',
          fontFamily: 'inherit',
          lineHeight: 1.5,
          borderRadius: '8px',
          border: `1px solid ${color.border}`,
          background: 'transparent',
          color: color.text,
          resize: 'vertical',
        }}
      />
    </label>
  );
}

// ------------------------------------------------------------- helpers

/**
 * Converte a série da RPC no formato do TrendChart, ou devolve null quando não
 * há forma suficiente para um gráfico honesto.
 *
 * O piso de 5 pontos é o mesmo raciocínio da cobertura no brief: dois ou três
 * dias desenham uma reta que parece tendência, e o paciente lê o documento
 * sozinho, sem ninguém para dizer que aquilo é ruído.
 */
function montarGrafico(
  titulo: string,
  unidade: string,
  linhas: { bucket: string; min_value: number; max_value: number; avg_value: number }[] | undefined,
  converter: (v: number) => number = (v) => v,
) {
  if (!linhas || linhas.length < 5) return null;
  return {
    titulo,
    unidade,
    pontos: linhas.map((l) => ({
      bucket: l.bucket,
      min: converter(l.min_value),
      max: converter(l.max_value),
      avg: converter(l.avg_value),
    })),
  };
}

/** Segundos em "7h43" — como a pessoa fala, não como o banco guarda. */
function formatarHoras(segundos: number): string {
  const total = Math.round(segundos / 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
}
